// The combobox pattern (WAI-ARIA 1.2): an input with role="combobox", aria-expanded, aria-controls and
// aria-activedescendant over a role="listbox" of role="option" items. Focus stays in the input; ArrowDown/ArrowUp
// open and move, Home/End jump while open, Enter picks, Escape closes. The caller renders the listbox (portalled
// through McPortal) only while open, and filters `count` to what it shows.

import { useCallback, useEffect, useState, type KeyboardEvent, type MouseEvent } from "react";
import { useSvgId } from "./useSvgId";

export interface ListboxOptions {
  count: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (index: number) => void;
  /** The chosen item, highlighted first when the list opens. */
  selected?: number | undefined;
}

export interface Listbox {
  input: {
    role: "combobox";
    "aria-expanded": boolean;
    "aria-controls": string;
    "aria-autocomplete": "list";
    "aria-activedescendant"?: string;
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  };
  listbox: { id: string; role: "listbox" };
  option: (index: number) => {
    id: string;
    role: "option";
    "aria-selected": boolean;
    onMouseDown: (event: MouseEvent) => void;
    onClick: () => void;
  };
  /** The highlighted option, -1 when none. */
  active: number;
  setActive: (index: number) => void;
}

export function useListbox({ count, open, onOpenChange, onPick, selected }: ListboxOptions): Listbox {
  const base = useSvgId();
  const listId = `${base}-list`;
  const [active, setActive] = useState(-1);

  useEffect(() => {
    if (!open) setActive(-1);
    else setActive((current) => (current >= 0 && current < count ? current : selected !== undefined && selected >= 0 && selected < count ? selected : count > 0 ? 0 : -1));
  }, [open, count, selected]);

  const pick = useCallback((index: number) => {
    if (index < 0 || index >= count) return;
    onPick(index);
    onOpenChange(false);
  }, [count, onPick, onOpenChange]);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!open) onOpenChange(true);
        else if (count > 0) setActive((active + 1) % count);
        return;
      case "ArrowUp":
        event.preventDefault();
        if (!open) onOpenChange(true);
        else if (count > 0) setActive(active <= 0 ? count - 1 : active - 1);
        return;
      case "Home":
        if (open && count > 0) { event.preventDefault(); setActive(0); }
        return;
      case "End":
        if (open && count > 0) { event.preventDefault(); setActive(count - 1); }
        return;
      case "Enter":
        if (open && active >= 0) { event.preventDefault(); pick(active); }
        return;
      case "Escape":
        if (open) { event.preventDefault(); onOpenChange(false); }
        return;
      default:
    }
  };

  const optionId = (index: number) => `${base}-opt-${index}`;
  return {
    input: {
      role: "combobox",
      "aria-expanded": open,
      "aria-controls": listId,
      "aria-autocomplete": "list",
      ...(open && active >= 0 ? { "aria-activedescendant": optionId(active) } : {}),
      onKeyDown,
    },
    listbox: { id: listId, role: "listbox" },
    option: (index) => ({
      id: optionId(index),
      role: "option",
      "aria-selected": index === active,
      // Keep focus in the input while the pointer picks.
      onMouseDown: (event) => event.preventDefault(),
      onClick: () => pick(index),
    }),
    active,
    setActive,
  };
}
